/**
 * The Earth globe on the centre stage. Mounts the Cesium engine once and keeps
 * it in step with the loader store (data, filters, view settings, selection,
 * live contacts) and the shared clock.
 */
import { useEffect, useRef, useState } from 'react';
import { clockNow, useClock, useUI } from '@/state/store';
import { toast } from '@/components/ui/toast';
import { mergeContactPairs } from '../awareness/dsn';
import { useAwareness } from '../awareness/store';
import { getOrbitEngine } from '../engine';
import { isLiveNetwork, useLoader } from '../store';
import { ANALYSIS } from '../constants';
import { usePresentation } from '../ui/presentation';
import { useWeatherImagery } from '../weather/useWeatherImagery';
import { useWeatherOverlay } from '../weather/store';
import type { GlobeEngine, GlobeSelection } from './GlobeEngine';
import { globeEngineHolder } from './engineHandle';
import { useSSA } from '../ssa/store';

const CLOCK_RESYNC_TOLERANCE_MS = 2000;

/** Antennas connected to each mission for the selection-visibility rule (passes, else windows). */
function connectionsFromResults(
  results: ReturnType<typeof useLoader.getState>['results'],
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  if (!results) return out;
  for (const [missionId, sla] of Object.entries(results.missionSLA)) {
    const ids = Object.entries(sla.perAntenna)
      .filter(([, v]) => v.passes > 0)
      .map(([id]) => id);
    if (ids.length) out[missionId] = ids;
  }
  for (const [missionId, byAntenna] of Object.entries(results.contactWindows)) {
    if (out[missionId]) continue;
    const ids = Object.entries(byAntenna)
      .filter(([, w]) => w.length > 0)
      .map(([id]) => id);
    if (ids.length) out[missionId] = ids;
  }
  return out;
}

export function LoaderGlobe({ parked = false }: { parked?: boolean }) {
  useWeatherImagery(parked);
  const containerRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<GlobeEngine | null>(null);
  const applyingSelection = useRef(false);
  const [ready, setReady] = useState(false);
  const [graphicsLost, setGraphicsLost] = useState(false);
  const [generation, setGeneration] = useState(0);
  /** Missions the engine actually drew (L1 and no-TLE missions are skipped). */
  const loadedMissionIds = useRef<Set<string>>(new Set());

  const dataset = useLoader((s) => s.dataset);
  const results = useLoader((s) => s.results);
  const activeMissionIds = useLoader((s) => s.activeMissionIds);
  const activeAntennaIds = useLoader((s) => s.activeAntennaIds);
  const visibleMissionIds = useLoader((s) => s.visibleMissionIds);
  const visibleAntennaIds = useLoader((s) => s.visibleAntennaIds);
  const sceneMode = useLoader((s) => s.sceneMode);
  const mapStyle = useLoader((s) => s.mapStyle);
  const isFullCatalogue=useLoader(s=>s.network==='full_catalogue');
  const catalogueStyle=useSSA(s=>s.style),catalogueTumble=useSSA(s=>s.tumble),catalogueLabels=useSSA(s=>s.labels&&!s.alertsOnly);
  useEffect(()=>{if(ready)engineRef.current?.setCatalogueDisplay?.({style:isFullCatalogue?catalogueStyle:'dots',tumble:isFullCatalogue&&catalogueTumble,labels:!isFullCatalogue||catalogueLabels});},[ready,isFullCatalogue,catalogueStyle,catalogueTumble,catalogueLabels,dataset]);
  const dayNight = useLoader((s) => s.network !== 'full_catalogue' && s.dayNight);
  const orbitPathMode = useLoader((s) => s.orbitPathMode);
  const coverageEnabled = useLoader((s) => s.coverageEnabled);
  const coneMode = useLoader((s) => s.coneMode);
  const contactLinesEnabled = useLoader((s) => s.contactLinesEnabled);
  const contactStyle = usePresentation((s) => s.contactStyle);
  const coverageRingsEnabled = useLoader((s) => s.coverageRingsEnabled);
  const nightLights = useLoader((s) => s.nightLights);
  const google3D = useLoader((s) => s.network !== 'full_catalogue' && s.google3D);
  const osmBuildings = useLoader((s) => s.network !== 'full_catalogue' && s.osmBuildings);
  const terrain = useLoader((s) => s.network !== 'full_catalogue' && s.terrain);
  const tdrssShown = useLoader((s) => s.tdrssShown);
  const weatherLayer = useLoader((s) => s.weatherLayer);
  const weatherOpacity = useLoader((s) => s.weatherOpacity);
  const weatherRequest = useWeatherOverlay((s) => s.request);
  const trackedMissionId = useLoader((s) => s.trackedMissionId);
  const activeContacts = useLoader((s) => s.activeContacts);
  const dsnPairs = useAwareness((s) => s.dsnContactPairs);
  const selection = useUI((s) => s.selection);
  const live = useClock((s) => s.live);
  const playing = useClock((s) => s.playing);
  const multiplier = useClock((s) => s.multiplier);
  const timeframe = useLoader((s) => s.timeframe);

  // Mount once. CesiumGlobe, and Cesium with it, is imported here instead of at
  // module scope, so the engine loads on the first globe mode rather than with the
  // shell (docs/PERFORMANCE.md). An unmount before the import resolves creates nothing.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let disposed = false;
    let stopEngine: (() => void) | null = null;

    function startEngine(engine: GlobeEngine, target: HTMLDivElement): () => void {
      engineRef.current = engine;
      globeEngineHolder.current = engine;
      const st = useLoader.getState();
      engine
        .mount(target, {
          ionToken: import.meta.env.VITE_CESIUM_ION_TOKEN as string | undefined,
          worldTerrain: st.network !== 'full_catalogue' && st.terrain,
          ionAssets: true,
          sceneMode: st.sceneMode,
          mapStyle: st.mapStyle,
          bulkPropagator: getOrbitEngine(),
        })
        .then(() => {
          if (!disposed) setReady(true);
        })
        .catch((err: unknown) => {
          console.error('[globe] mount failed:', err);
          toast('error', 'The globe failed to start; see the console.');
        });
      const offSelect = engine.on('select', (sel: GlobeSelection | null) => {
        if (applyingSelection.current) return;
        if (!sel) {
          useUI.getState().select({ missionId: null, antennaId: null });
          return;
        }
        if (sel.kind === 'mission' || sel.kind === 'overlay' ||
          (sel.kind === 'horizons' && useLoader.getState().dataset?.missions.some((m) => m.id === sel.id)))
          useUI.getState().select({ missionId: sel.id, antennaId: null });
        else if (sel.kind === 'antenna') useUI.getState().select({ antennaId: sel.id, missionId: null });
        else if (sel.kind === 'tdrs') useUI.getState().select({ missionId: sel.id, antennaId: null });
      });
      const offStatus = engine.on('status', (m) =>
        toast(m.level === 'error' ? 'error' : m.level === 'warn' ? 'warn' : 'info', m.text),
      );
      const offContact = engine.on('contactSelect', (pair) => {
        useUI.setState({ rightPanelOpen: true });
        useUI.getState().select({ ...pair, assetId: null });
      });
      const offRewind = engine.on('rewind', (ms) => useLoader.getState().trimAosLosAfter(ms));
      const offMode = engine.on('sceneModeChanged', (mode) => {
        if (useLoader.getState().sceneMode !== mode) useLoader.getState().setView({ sceneMode: mode });
      });
      return () => {
        offSelect();
        offContact();
        offStatus();
        offRewind();
        offMode();
        engine.destroy();
        engineRef.current = null;
        globeEngineHolder.current = null;
      };
    }

    void import('./CesiumGlobe')
      .then(({ createCesiumGlobe }) => {
        if (!disposed) stopEngine = startEngine(createCesiumGlobe(), container);
      })
      .catch((err: unknown) => {
        console.error('[globe] the Cesium engine failed to load or start:', err);
        toast('error', 'The globe failed to start; see the console.');
      });

    return () => {
      disposed = true;
      stopEngine?.();
      useAwareness.getState().setEngineMounted(false);
      setReady(false);
    };
  }, [generation]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const lost = (event: Event) => {
      event.preventDefault();
      engineRef.current?.setRendering?.(false);
      setGraphicsLost(true);
    };
    container.addEventListener('webglcontextlost', lost, true);
    return () => container.removeEventListener('webglcontextlost', lost, true);
  }, []);

  // Data: antennas then missions (clearAll on a dataset swap).
  useEffect(() => {
    const engine = engineRef.current;
    if (!ready || !engine) return;
    // Rebuilding primitives for a new revision or briefing must not clear the
    // application's selected identity. The selection effect restores its view.
    applyingSelection.current=true;
    try { engine.clearAll(); } finally { applyingSelection.current=false; }
    if (!dataset) {
      loadedMissionIds.current.clear();
      return;
    }
    engine.setAntennas(dataset.antennas, dataset.missions);
    const report = engine.setMissions(dataset.missions, {
      catalogIds: new Set(dataset.missions.filter((m) => m.isCatalog).map((m) => m.id)),
    });
    loadedMissionIds.current = new Set(report.loaded);
    if (report.failed.length > 0) {
      toast(
        'warn',
        `${report.failed.length} mission${report.failed.length === 1 ? '' : 's'} could not be propagated: ${report.failed
          .slice(0, 3)
          .map((f) => f.id)
          .join(', ')}${report.failed.length > 3 ? ' and more' : ''}.`,
      );
    }
    engine.setTdrss(useLoader.getState().tdrssShown ? dataset.tdrss : null);
    // The awareness layers redraw after every clear: announce the engine and bump its epoch.
    const awareness = useAwareness.getState();
    awareness.setEngineMounted(true);
    awareness.bumpEngineEpoch();
  }, [ready, dataset]);

  // Visibility: active (what-if) and filtered.
  useEffect(() => {
    const engine = engineRef.current;
    if (!ready || !engine || !dataset) return;
    const activeA = new Set(activeAntennaIds);
    const visibleA = visibleAntennaIds ? new Set(visibleAntennaIds) : null;
    for (const a of dataset.antennas)
      engine.setAntennaVisible(a.id, activeA.has(a.id) && (!visibleA || visibleA.has(a.id)));
    const activeM = new Set(activeMissionIds);
    const visibleM = visibleMissionIds ? new Set(visibleMissionIds) : null;
    const constellation =
      isLiveNetwork(dataset.network) && dataset.missions.length > ANALYSIS.LARGE_NETWORK_THRESHOLD;
    for (const m of dataset.missions) {
      if (!loadedMissionIds.current.has(m.id)) continue;
      const shown = !visibleM || visibleM.has(m.id);
      // The first-100 planning default must not erase the rest of a live
      // constellation. Context points remain pickable; explicit Hide still wins.
      engine.setMissionVisible(
        m.id,
        activeM.has(m.id) && shown,
        constellation && shown && !activeM.has(m.id),
      );
    }
  }, [ready, dataset, activeAntennaIds, activeMissionIds, visibleAntennaIds, visibleMissionIds]);

  useEffect(() => {
    const engine = engineRef.current;
    if (!ready || !engine) return;
    engine.setConnections(connectionsFromResults(results));
  }, [ready, results]);

  // View settings.
  useEffect(() => {
    if (ready) engineRef.current?.setContactStyle?.(contactStyle);
  }, [ready, contactStyle]);
  useEffect(() => {
    const engine = engineRef.current;
    if (ready && engine && engine.getSceneMode() !== sceneMode) engine.setSceneMode(sceneMode);
  }, [ready, sceneMode]);
  useEffect(() => {
    if (ready) engineRef.current?.setMapStyle(mapStyle);
  }, [ready, mapStyle]);
  useEffect(() => {
    if (ready) engineRef.current?.setDayNight(dayNight);
  }, [ready, dayNight]);
  useEffect(() => {
    if (ready) engineRef.current?.setOrbitPathMode(orbitPathMode);
  }, [ready, orbitPathMode]);
  useEffect(() => {
    if (ready) engineRef.current?.setCoverageEnabled(coverageEnabled);
  }, [ready, coverageEnabled]);
  useEffect(() => {
    if (ready) engineRef.current?.setConeMode(coneMode);
  }, [ready, coneMode]);
  useEffect(() => {
    if (ready) engineRef.current?.setContactLinesEnabled(contactLinesEnabled);
  }, [ready, contactLinesEnabled]);
  useEffect(() => {
    if (ready) engineRef.current?.setCoverageRingsEnabled(coverageRingsEnabled);
  }, [ready, coverageRingsEnabled]);
  useEffect(() => {
    if (ready)
      engineRef.current
        ?.setNightLights(nightLights && dayNight)
        .catch((err: unknown) => console.error('[globe] night lights failed:', err));
  }, [ready, nightLights, dayNight]);
  useEffect(() => {
    if (ready)
      engineRef.current?.setGoogle3D(
        google3D && (mapStyle === 'satellite' || mapStyle === 'satellite-labels'),
      );
  }, [ready, google3D, mapStyle]);
  useEffect(() => {
    if (ready)
      engineRef.current
        ?.setOsmBuildings(osmBuildings)
        .catch((err: unknown) => console.error('[globe] OSM buildings failed:', err));
  }, [ready, osmBuildings]);
  useEffect(() => {
    if (ready)
      engineRef.current
        ?.setTerrainEnabled(terrain)
        .catch((err: unknown) => console.error('[globe] terrain failed:', err));
  }, [ready, terrain]);
  useEffect(() => {
    if (ready && dataset) engineRef.current?.setTdrss(tdrssShown ? dataset.tdrss : null);
  }, [ready, dataset, tdrssShown]);
  useEffect(() => {
    if (ready)
      engineRef.current?.setWeatherLayer(
        weatherRequest?.key === weatherLayer ? weatherLayer : null,
        weatherOpacity,
        {
          time: weatherRequest?.time,
          revision: weatherRequest?.revision,
          onError: (tileError) => useWeatherOverlay.setState({ tileError }),
        },
      );
  }, [ready, weatherLayer, weatherOpacity, weatherRequest]);

  // Selection and tracking.
  useEffect(() => {
    const engine = engineRef.current;
    if (!ready || !engine) return;
    const next: GlobeSelection | null = selection.missionId
      ? { kind: 'mission', id: selection.missionId }
      : selection.antennaId
        ? { kind: 'antenna', id: selection.antennaId }
        : null;
    const current = engine.getSelection();
    // Preserve which rendered entity was picked when a relay/deep-space marker
    // shares an id with a mission record. Its other representation may be hidden.
    const sameKind = current?.kind === next?.kind || (next?.kind === 'mission' && (current?.kind === 'tdrs' || current?.kind === 'horizons'));
    if (!sameKind || (current?.id ?? null) !== (next?.id ?? null)) {
      applyingSelection.current = true;
      try {
        engine.select(next);
      } finally {
        applyingSelection.current = false;
      }
    }
  }, [ready, dataset, selection.missionId, selection.antennaId]);
  useEffect(() => {
    const engine = engineRef.current;
    if (!ready || !engine) return;
    if (engine.getTracked() !== trackedMissionId) engine.track(trackedMissionId);
  }, [ready, dataset, trackedMissionId]);

  // Live contacts from the runtime tick.
  useEffect(() => {
    if (ready)
      engineRef.current?.setActiveContacts(
        mergeContactPairs(
          activeContacts.map((c) => ({ missionId: c.missionId, antennaId: c.antennaId })),
          dsnPairs,
        ),
      );
  }, [ready, activeContacts, dsnPairs]);

  // Clock: the shared conductor leads; Cesium follows at the same rate and
  // resyncs on jumps, pauses and multiplier changes.
  useEffect(() => {
    const engine = engineRef.current;
    if (!ready || !engine) return;
    const tf = timeframe;
    engine.setClock({
      startMs: tf.startMs,
      stopMs: tf.endMs,
      currentMs: clockNow(),
      multiplier: live ? 1 : multiplier,
      loop: false,
    });
    engine.setAnimating(!parked && (live || playing));
    engine.setRendering?.(!parked);
  }, [ready, live, playing, multiplier, parked, timeframe]);
  useEffect(() => {
    if (!ready) return;
    const unsub = useClock.subscribe((s) => {
      const engine = engineRef.current;
      if (!engine) return;
      const now = s.live ? Date.now() : s.simTime;
      if (Math.abs(engine.getCurrentTime() - now) > CLOCK_RESYNC_TOLERANCE_MS) engine.setCurrentTime(now);
    });
    return unsub;
  }, [ready]);

  // Resize with the stage.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => engineRef.current?.resize());
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  // Keyboard: F follows the selected mission, Escape stops tracking.
  useEffect(() => {
    if (parked) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)
      )
        return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const st = useLoader.getState();
      if (st.network === 'full_catalogue') {
        if ((e.key === 'f' || e.key === 'F') && useUI.getState().selection.missionId) { e.preventDefault(); useSSA.setState(s => ({ follow: !s.follow })); }
        else if (e.key === 'Escape' && !e.defaultPrevented) useSSA.setState({ follow: false });
        return;
      }
      if (e.key === 'f' || e.key === 'F') {
        const sel = useUI.getState().selection.missionId;
        if (sel) st.setTracked(st.trackedMissionId === sel ? null : sel);
      } else if (e.key === 'Escape' && !e.defaultPrevented && st.trackedMissionId) {
        // A popover, menu or the floating strip that handled this Escape keeps the camera where it is.
        st.setTracked(null);
        engineRef.current?.resetView();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [parked]);

  return <><div ref={containerRef} className="absolute inset-0 bg-canvas" data-testid="loader-globe" />
    {graphicsLost && !parked && <div role="alert" className="absolute inset-0 z-30 flex items-center justify-center bg-canvas/80"><div className="rounded-panel border border-hairline bg-raised p-5 text-sm"><p>The graphics session was interrupted. Your catalogue and selection are retained.</p><button className="mt-3 rounded border border-accent px-3 py-2 text-accent" onClick={() => { setReady(false); setGraphicsLost(false); useSSA.setState(s=>({focusRequest:s.focusRequest+1})); setGeneration(g=>g+1); }}>Restore globe</button></div></div>}
  </>;
}
